import React from 'react';
import { Team } from '../types';
import {
  ChildrenRow,
  CollapseButton,
  CollapseIcon,
  HorizontalLine,
  LeaderText,
  MemberCount,
  NodeBox,
  NodeButton,
  NodeCard,
  NodeFilterButton,
  NodeMenuButton,
  NodeWrapper,
  TitleBar,
  TitleText,
  VerticalConnector,
  TreeChildrenColumn,
  TreeChildItem,
  TreeParentConnector,
  NodeInfoRow,
} from './TeamOrgStructure.styles';
import { isColorDark } from './TeamOrgStructureUtils';

interface TeamOrgNodeProps {
  team: Team;
  childrenMap: Map<number, Team[]>;
  onEdit: (team: Team) => void;
  onManageClick: (team: Team) => void;
  onOpenMenu: (team: Team, target: HTMLElement) => void;
  onFilterSelect: (team: Team | null) => void;
  selectedTeamId: number | null;
  dropTargetId: number | null;
  onDragStart: (e: React.DragEvent, team: Team) => void;
  onDragOverBox: (e: React.DragEvent, team: Team) => void;
  onDragLeaveBox: (e: React.DragEvent, team: Team) => void;
  onDropOnBox: (e: React.DragEvent, team: Team) => void;
  collapsedTeamIds: Set<number>;
  onToggleCollapse: (teamId: number) => void;
  layoutMode?: 'horizontal' | 'tree';
  compactView?: boolean;
}

export const TeamOrgNode: React.FC<TeamOrgNodeProps> = ({
  team,
  childrenMap,
  onEdit,
  onManageClick,
  onOpenMenu,
  onFilterSelect,
  selectedTeamId,
  dropTargetId,
  onDragStart,
  onDragOverBox,
  onDragLeaveBox,
  onDropOnBox,
  collapsedTeamIds,
  onToggleCollapse,
  layoutMode = 'horizontal',
  compactView = false,
}) => {
  const children = childrenMap.get(team.id) || [];
  const hasChildren = children.length > 0;
  const isCollapsed = collapsedTeamIds.has(team.id);
  const isTreeMode = layoutMode === 'tree';

  return (
    <NodeWrapper className="org-node-wrapper" $layoutMode={layoutMode}>
      <NodeCard>
        <NodeButton
          role="button"
          tabIndex={0}
          onClick={() => onEdit(team)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onEdit(team);
            }
          }}
        >
          <NodeBox
            $layoutMode={layoutMode}
            className={`org-node-box ${dropTargetId === team.id ? 'drop-target' : ''}`}
            onDragOver={(e) => onDragOverBox(e, team)}
            onDragEnter={(e) => onDragOverBox(e, team)}
            onDragLeave={(e) => onDragLeaveBox(e, team)}
            onDrop={(e) => onDropOnBox(e, team)}
          >
            <NodeFilterButton
              $layoutMode={layoutMode}
              $useWhiteIcons={Boolean(team.color && isColorDark(team.color))}
              title={selectedTeamId === team.id ? 'Reset filter to All Teams' : 'Filter org chart to this team'}
              aria-label={selectedTeamId === team.id ? 'Reset filter to All Teams' : `Filter org chart to ${team.name}`}
              onClick={(e) => {
                e.stopPropagation();
                onFilterSelect(selectedTeamId === team.id ? null : team);
              }}
            >
              {selectedTeamId === team.id ? '↑' : '⦿'}
            </NodeFilterButton>
            <TitleBar
              $color={team.color}
              $layoutMode={layoutMode}
              draggable
              onDragStart={(e) => onDragStart(e, team)}
              title={(isTreeMode || compactView)
                ? `${team.name}\n${team.leader_name || 'No leader'}\n${team.member_count} member${team.member_count !== 1 ? 's' : ''}`
                : 'Drag to reparent'}
              aria-label={(isTreeMode || compactView)
                ? `Details for ${team.name}`
                : 'Drag to reparent'}
            >
              <TitleText $layoutMode={layoutMode}>{team.name}</TitleText>
            </TitleBar>

            <NodeMenuButton
              className="org-node-menu-button"
              $layoutMode={layoutMode}
              $useWhiteIcons={Boolean(team.color && isColorDark(team.color))}
              onClick={(e) => {
                e.stopPropagation();
                const target = e.currentTarget as HTMLElement;
                onOpenMenu(team, target);
              }}
              aria-haspopup="true"
              aria-label="Open team menu"
            >
              ⋮
            </NodeMenuButton>

            {!compactView && (
              isTreeMode ? (
                <NodeInfoRow>
                  <LeaderText>{team.leader_name || 'No leader'}</LeaderText>
                  <MemberCount
                    $layoutMode={layoutMode}
                    role="button"
                    tabIndex={0}
                    title="Manage members"
                    aria-label={`Manage members for ${team.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageClick(team);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        onManageClick(team);
                      }
                    }}
                  >
                    {team.member_count} member{team.member_count !== 1 ? 's' : ''}
                  </MemberCount>
                </NodeInfoRow>
              ) : (
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ marginTop: 4 }}>
                    <LeaderText>{team.leader_name || 'No leader'}</LeaderText>
                  </div>
                  <MemberCount
                    role="button"
                    tabIndex={0}
                    title="Manage members"
                    aria-label={`Manage members for ${team.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageClick(team);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        onManageClick(team);
                      }
                    }}
                  >
                    {team.member_count} member{team.member_count !== 1 ? 's' : ''}
                  </MemberCount>
                </div>
              )
            )}
          </NodeBox>
        </NodeButton>

        {hasChildren && (
          <CollapseButton
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onToggleCollapse(team.id);
            }}
            aria-label={isCollapsed ? `Expand ${team.name}` : `Collapse ${team.name}`}
            title={isCollapsed ? `Expand ${team.name}` : `Collapse ${team.name}`}
          >
            <CollapseIcon $hideOnDefault={!isCollapsed}>{isCollapsed ? '+' : '-'}</CollapseIcon>
          </CollapseButton>
        )}

        {!isCollapsed && hasChildren && isTreeMode && <TreeParentConnector />}
      </NodeCard>

      {!isCollapsed && hasChildren && !isTreeMode && <VerticalConnector />}
      {!isCollapsed && hasChildren && (
        isTreeMode ? (
          <TreeChildrenColumn $compactView={compactView}>
              {children.map((child) => (
                <TreeChildItem key={child.id}>
                  <TeamOrgNode
                    team={child}
                    childrenMap={childrenMap}
                    onEdit={onEdit}
                    onManageClick={onManageClick}
                    onOpenMenu={onOpenMenu}
                    onFilterSelect={onFilterSelect}
                    selectedTeamId={selectedTeamId}
                    dropTargetId={dropTargetId}
                    onDragStart={onDragStart}
                    onDragOverBox={onDragOverBox}
                    onDragLeaveBox={onDragLeaveBox}
                    onDropOnBox={onDropOnBox}
                    collapsedTeamIds={collapsedTeamIds}
                    onToggleCollapse={onToggleCollapse}
                    layoutMode={layoutMode}
                    compactView={compactView}
                  />
                </TreeChildItem>
              ))}
            </TreeChildrenColumn>
        ) : (
          <ChildrenRow>
            <HorizontalLine />
            {children.map((child) => (
              <div key={child.id} style={{ position: 'relative' }}>
                <div
                  style={{
                    position: 'absolute',
                    top: -18,
                    left: '50%',
                    width: 2,
                    height: 18,
                    background: '#e6e6e6',
                    transform: 'translateX(-50%)',
                    zIndex: 1,
                  }}
                />
                <TeamOrgNode
                  team={child}
                  childrenMap={childrenMap}
                  onEdit={onEdit}
                  onManageClick={onManageClick}
                  onOpenMenu={onOpenMenu}
                  onFilterSelect={onFilterSelect}
                  selectedTeamId={selectedTeamId}
                  dropTargetId={dropTargetId}
                  onDragStart={onDragStart}
                  onDragOverBox={onDragOverBox}
                  onDragLeaveBox={onDragLeaveBox}
                  onDropOnBox={onDropOnBox}
                  collapsedTeamIds={collapsedTeamIds}
                  onToggleCollapse={onToggleCollapse}
                  layoutMode={layoutMode}
                  compactView={compactView}
                />
              </div>
            ))}
          </ChildrenRow>
        )
      )}
    </NodeWrapper>
  );
};

interface IndependentTeamCardProps {
  team: Team;
  dropTargetId: number | null;
  onOpenMenu: (team: Team, target: HTMLElement) => void;
  onEdit: (team: Team) => void;
  setManageTeamId: (teamId: number) => void;
  onFilterSelect: (team: Team) => void;
  onDragStart: (e: React.DragEvent, team: Team) => void;
  onDragOverBox: (e: React.DragEvent, team: Team) => void;
  onDragLeaveBox: (e: React.DragEvent, team: Team) => void;
  onDropOnBox: (e: React.DragEvent, team: Team) => void;
}

export const IndependentTeamCard: React.FC<IndependentTeamCardProps> = ({
  team,
  dropTargetId,
  onOpenMenu,
  onEdit,
  setManageTeamId,
  onFilterSelect,
  onDragStart,
  onDragOverBox,
  onDragLeaveBox,
  onDropOnBox,
}) => (
  <NodeWrapper className="org-node-wrapper" key={team.id}>
    <NodeButton
      role="button"
      tabIndex={0}
      onClick={() => onEdit(team)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEdit(team);
        }
      }}
    >
      <NodeBox
        className={`org-node-box ${dropTargetId === team.id ? 'drop-target' : ''}`}
        onDragOver={(e) => onDragOverBox(e, team)}
        onDragEnter={(e) => onDragOverBox(e, team)}
        onDragLeave={(e) => onDragLeaveBox(e, team)}
        onDrop={(e) => onDropOnBox(e, team)}
      >
          <NodeFilterButton
            title="Filter org chart to this team"
            aria-label={`Filter org chart to ${team.name}`}
            onClick={(e) => {
              e.stopPropagation();
              onFilterSelect(team);
            }}
          >
            ⦿
          </NodeFilterButton>

          <TitleBar
            $color={team.color}
            draggable
            onDragStart={(e) => onDragStart(e, team)}
            title="Drag to reparent"
            aria-label="Drag to reparent"
          >
            <TitleText>{team.name}</TitleText>
          </TitleBar>

          <NodeMenuButton
            className="org-node-menu-button"
            onClick={(e) => {
              e.stopPropagation();
              const target = e.currentTarget as HTMLElement;
              onOpenMenu(team, target);
            }}
            aria-label="Open team menu"
          >
            ⋮
          </NodeMenuButton>

          <div style={{ padding: '10px 12px' }}>
            <div style={{ marginTop: 4 }}>
              <LeaderText>{team.leader_name || 'No leader'}</LeaderText>
            </div>
            <MemberCount
              role="button"
              tabIndex={0}
              title="Manage members"
              aria-label={`Manage members for ${team.name}`}
              onClick={(e) => {
                e.stopPropagation();
                setManageTeamId(team.id);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  setManageTeamId(team.id);
                }
              }}
            >
              {team.member_count} member{team.member_count !== 1 ? 's' : ''}
            </MemberCount>
          </div>
        </NodeBox>
      </NodeButton>
    </NodeWrapper>
);
